import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot } from '@angular/router';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { catchError, map } from 'rxjs/operators';
import { throwError, Observable, firstValueFrom, of } from 'rxjs';
import { WorkRequestListComponent } from '../pages/work-request-list/work-request-list.component';
import { JwtHelperService } from '@auth0/angular-jwt';
import { StorageService } from './storage.service';
import { DataService } from './data.service';

export interface IUser {
	id: number;
	email: string;
	name?: string;
	avatarUrl?: string;
	primaryTenantId: number;
}

export interface IResponse {
	isOk: boolean;
	data?: IUser;
	message?: string;
}

interface ILoginResponse {
	requiresTwoFactor?: boolean;
	tokenString?: string | null;
	resetPassword?: boolean;
}

const defaultPath = '/';

@Injectable()
export class AuthService {

	private _router: Router;
	private _httpClient: HttpClient;
	private _storageService: StorageService;
	private _jwtHelper: JwtHelperService;
	private _user: IUser | null = null;
	private _lastAuthenticatedPath: string = defaultPath;
	private _token: string | null = null;
	private _tfcData: any;

	public get isTokenValid(): boolean {
		if (!this._token) {
			return false;
		}
		return !this._jwtHelper.isTokenExpired(this._token);
	}

	public get token(): string | null {
		return this._token;
	}

	public get loggedIn(): boolean {
		return !!this._user;
	}

	public set lastAuthenticatedPath(value: string) {
		this._lastAuthenticatedPath = value;
	}

	constructor(router: Router, 
		httpClient: HttpClient,
		storageService: StorageService,
		jwtHelper: JwtHelperService) {
		this._router = router;
		this._httpClient = httpClient;
		this._storageService = storageService;
		this._jwtHelper = jwtHelper;

//		this.setToken(localStorage.getItem(this._tokenKey) || sessionStorage.getItem(this._tokenKey), false);
		this.setToken(this._storageService.getToken(), false);
	}

	private setToken(token: string, rememberMe: boolean) {
		if (this._jwtHelper.isTokenExpired(token)) {
			this._user = null;
			this._token = null;
			this._storageService.clearToken();
		} else {
			const payload = this._jwtHelper.decodeToken(token);
//			console.log(payload);
			this._user = {
				id: payload.nameid,
				email: payload.email,
				name: null,
				avatarUrl: null,
				primaryTenantId: 0
			};
			this._token = token;
			this._storageService.setToken(this._token, rememberMe);
		}
	}

	public async logInTfc(twoFactorCode: string) {
		const result = this.logIn(this._tfcData.email, this._tfcData.password, twoFactorCode, this._tfcData.rememberMe);
		this._tfcData = null;
		return result;
	}
	
	public async logIn(
		email: string, 
		password: string,
		twoFactorCode: string | null,
		rememberMe: boolean
	) {
		try {
			const requestBody = {
				username: email,
				password: password,
				twoFactorCode: twoFactorCode
			};

//			console.log(requestBody);

			const response = await firstValueFrom(
				this._httpClient.post<ILoginResponse>(`${environment.apiUrl}/Auth/login`, requestBody));

//			console.log(response);

			if (response?.requiresTwoFactor) {
				this._tfcData = { email: email, password: password, rememberMe: rememberMe };
				this._router.navigate(['/auth/two-factor-code']);
				return {
					isOk: true,
				};
			}

			if (response?.tokenString) {
				this.setToken(response.tokenString, rememberMe);

				if (response?.resetPassword) {
					this._router.navigate(['/auth/change-password']);
					return {
						isOk: true,
					};
				}
	
				this._router.navigate([this._lastAuthenticatedPath]);
				this.getUser();

				return {
					isOk: true,
					data: this._user,
				};
			}

			this._user = null;
			this._token = null;

			throw {};
		} catch (error) {
			let errorMessage: string;
			if (error instanceof HttpErrorResponse) {
				if (error.status === 401) {
					if (twoFactorCode === null)
						errorMessage = 'Helytelen felhasználónév vagy jelszó!';
					else
						errorMessage = 'Helytelen kétlépcsős azonosító!';
				} else if (error.status === 404) {
					errorMessage = 'Erőforrás nem található (404)!';
				} else {
					errorMessage = `HTTP státusz ${error.status}`;
				}
			} else if (error instanceof Error) {
				errorMessage = `Hiba: ${error.message}`;
			} else {
				errorMessage = `Ismeretlen hiba. ${error}`;
				console.error(error);
			}			

			return {
				isOk: false,
				message: `Hiba a bejentkezés során: ${errorMessage}`,
			};
		}
	}

	public async getUser() {
		try {
			const response = await firstValueFrom(
				this._httpClient.get<any>(`${environment.apiUrl}/Auth/appuser`).pipe(
//					catchError((error: HttpErrorResponse) => { 
//						this.reset(); 
//						return of(null); 
//					})
				));
//			console.log('getUser - ', response);

			this._user.name = response.name;
			this._user.primaryTenantId = response.primaryTenantId;

			return {
				isOk: true,
				data: this._user,
			};
		} catch {
			return {
				isOk: false,
				data: null,
			};
		}
	}

	async createAccount(email: string, password: string) {
		try {
			this._router.navigate(['/auth/create-account']);
			return {
				isOk: true,
			};
		} catch {
			return {
				isOk: false,
				message: 'Failed to create account',
			};
		}
	}

	async createPassword(recoveryCode: string, password: string) {
		try {
			const response = await firstValueFrom(
				this._httpClient.post(`${environment.apiUrl}/Auth/create-password`, 
					{
						recoveryCode: recoveryCode,
						password: password
					}));
				
			return {
				isOk: true,
			};
		} catch {
			return {
				isOk: false,
				message: 'Hiba történt a jelszó létrehozása során',
			};
		}
	}

	async changePassword(oldPassword: string, newPassword: string) {
		try {
			const response = await firstValueFrom(
				this._httpClient.post(`${environment.apiUrl}/Auth/change-password`, 
					{
						oldPassword: oldPassword,
						newPassword: newPassword
					},
					{ 
						headers: new HttpHeaders({
							'Authorization': `Bearer ${this.token}`
						})
					})
			);
				
			return {
				isOk: true,
			};
		} catch {
			return {
				isOk: false,
				message: 'Hiba történt a jelszó módosítása során',
			};
		}
	}

	async resetPassword(email: string) {
		try {
			// Send request

			return {
				isOk: true,
			};
		} catch {
			return {
				isOk: false,
				message: 'Hiba történt a jelszó visszaállítása során',
			};
		}
	}

	public logOut() {
		this._httpClient.post(`${environment.apiUrl}/Auth/logout`, null);
		this.reset();
	}

	public reset() {
		this.setToken(null, false);
		this._token = null;
		this._user = null;
		this._router.navigate(['/auth/login']);
	}
}

@Injectable()
export class AuthGuardService implements CanActivate {
	constructor(private router: Router, private authService: AuthService) { }

	canActivate(route: ActivatedRouteSnapshot): boolean {
		const isLoggedIn = this.authService.loggedIn;
		const isAuthForm = [
			'login',
			'two-factor-code',
			'reset-password',
			'create-account',
			'create-password/:recoveryCode',
		].includes(route.routeConfig?.path || defaultPath);

		if (!isLoggedIn && !isAuthForm) {
			this.router.navigate(['/auth/login']);
		}

		if (isLoggedIn) {
			this.authService.lastAuthenticatedPath = route.routeConfig?.path || defaultPath;
		}

		return isLoggedIn || isAuthForm;
	}
}
